<template>
  <div class="company-leads-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="leads-container">
          <!-- Page Header -->
          <div class="page-header">
            <div class="header-left">
              <h1 class="page-title">
                <span class="page-icon">👥</span>
                Company Leads
              </h1>
              <p class="page-description">Manage required lead information for your company</p>
            </div>
            <div class="header-right">
              <button class="btn-create-custom" @click="openCreateModal">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
                Create Custom Field
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading lead types...</p>
          </div>

          <!-- Main Content -->
          <div v-else class="leads-content">
            <div class="leads-grid-container">

              <!-- Available to Add Section -->
              <div class="leads-section available-section">
                <h3 class="section-title">
                  <span class="section-icon">📋</span>
                  Available to Add
                  <span class="count-badge">{{ unassignedLeads.length }}</span>
                </h3>
                <p class="section-description">
                  Click to add these fields as requirements for lead collection.
                </p>

                <div v-if="unassignedLeads.length === 0" class="empty-section">
                  <div class="empty-icon">✅</div>
                  <p>All lead types are currently required</p>
                </div>

                <div v-else class="leads-list">
                  <div
                    v-for="lead in unassignedLeads"
                    :key="'available-' + lead.id"
                    class="lead-card available-card"
                    :class="{ 'processing': processingLeads.includes(lead.id) }"
                  >
                    <div class="lead-info">
                      <div class="lead-icon">{{ getLeadIcon(lead) }}</div>
                      <div class="lead-details">
                        <h4 class="lead-name">
                          {{ lead.name }}
                          <span v-if="lead.is_custom" class="custom-badge">Custom</span>
                        </h4>
                        <p class="lead-description">{{ getLeadDescription(lead) }}</p>
                      </div>
                    </div>

                    <div class="lead-actions">
                      <button
                        @click="assignLead(lead.id)"
                        :disabled="processingLeads.includes(lead.id)"
                        class="btn-assign"
                      >
                        <span v-if="processingLeads.includes(lead.id)" class="loading-spinner-small"></span>
                        <span v-else class="action-icon">+</span>
                        {{ processingLeads.includes(lead.id) ? 'Adding...' : 'Add' }}
                      </button>
                      <button
                        v-if="lead.is_custom"
                        @click="confirmDeleteCustomField(lead)"
                        :disabled="processingLeads.includes(lead.id)"
                        class="btn-delete-custom"
                        title="Delete this custom field"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Currently Required Section -->
              <div class="leads-section required-section">
                <h3 class="section-title">
                  <span class="section-icon">✅</span>
                  Currently Required
                  <span class="count-badge">{{ assignedLeads.length }}</span>
                </h3>
                <p class="section-description">
                  Click to remove these fields from your lead collection requirements.
                </p>

                <div v-if="assignedLeads.length === 0" class="empty-section">
                  <div class="empty-icon">📝</div>
                  <p>No lead requirements set yet</p>
                </div>

                <div v-else class="leads-list">
                  <div
                    v-for="lead in assignedLeadObjects"
                    :key="'assigned-' + lead.id"
                    class="lead-card required-card"
                    :class="{ 'processing': processingLeads.includes(lead.id) }"
                  >
                    <div class="lead-info">
                      <div class="lead-icon">{{ getLeadIcon(lead) }}</div>
                      <div class="lead-details">
                        <h4 class="lead-name">
                          {{ lead.name }}
                          <span v-if="lead.is_custom" class="custom-badge">Custom</span>
                        </h4>
                        <p class="lead-description">{{ getLeadDescription(lead) }}</p>
                      </div>
                    </div>

                    <div class="lead-actions">
                      <button
                        @click="removeLead(lead.id)"
                        :disabled="processingLeads.includes(lead.id)"
                        class="btn-remove"
                      >
                        <span v-if="processingLeads.includes(lead.id)" class="loading-spinner-small"></span>
                        <span v-else class="action-icon">-</span>
                        {{ processingLeads.includes(lead.id) ? 'Removing...' : 'Remove' }}
                      </button>
                      <button
                        v-if="lead.is_custom"
                        @click="confirmDeleteCustomField(lead)"
                        :disabled="processingLeads.includes(lead.id)"
                        class="btn-delete-custom"
                        title="Delete this custom field"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Create Custom Field Modal -->
    <div v-if="showCreateModal" class="modal-overlay" @click.self="closeCreateModal">
      <div class="modal-box">
        <div class="modal-header">
          <h2 class="modal-title">Create Custom Field</h2>
          <button class="modal-close" @click="closeCreateModal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">Field Name <span class="required-star">*</span></label>
            <input
              v-model="createForm.name"
              type="text"
              class="form-input"
              placeholder="e.g. LinkedIn Profile, Budget Range"
              maxlength="100"
              @keydown.enter="submitCreateForm"
            />
          </div>
          <div class="form-group">
            <label class="form-label">Field Type</label>
            <select v-model="createForm.field_type" class="form-select">
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="textarea">Long Text</option>
              <option value="select">Dropdown (Select)</option>
            </select>
          </div>
          <!-- Options for select type -->
          <div v-if="createForm.field_type === 'select'" class="form-group">
            <label class="form-label">Options <span class="required-star">*</span></label>
            <div
              v-for="(opt, idx) in createForm.field_options"
              :key="idx"
              class="option-row"
            >
              <input
                v-model="createForm.field_options[idx]"
                type="text"
                class="form-input option-input"
                :placeholder="'Option ' + (idx + 1)"
              />
              <button
                v-if="createForm.field_options.length > 1"
                @click="removeOption(idx)"
                class="btn-remove-option"
                title="Remove option"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <button class="btn-add-option" @click="addOption">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              Add Option
            </button>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeCreateModal" :disabled="creating">Cancel</button>
          <button class="btn-confirm-create" @click="submitCreateForm" :disabled="creating || !createForm.name.trim()">
            <span v-if="creating" class="loading-spinner-small"></span>
            {{ creating ? 'Creating...' : 'Create Field' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirm Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click.self="cancelDelete">
      <div class="modal-box modal-box-sm">
        <div class="modal-header">
          <h2 class="modal-title">Delete Custom Field</h2>
          <button class="modal-close" @click="cancelDelete">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <p class="delete-warning">
            Are you sure you want to delete the custom field <strong>"{{ deleteTarget && deleteTarget.name }}"</strong>?
            This will also remove it from your lead requirements.
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="cancelDelete" :disabled="deleting">Cancel</button>
          <button class="btn-confirm-delete" @click="executeDeleteCustomField" :disabled="deleting">
            <span v-if="deleting" class="loading-spinner-small"></span>
            {{ deleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Success Snackbar -->
    <div v-if="showSuccessNotification" class="snackbar success-snackbar">
      <div class="snackbar-content">
        <span class="snackbar-icon">✅</span>
        <span class="snackbar-message">{{ successMessage }}</span>
      </div>
      <button @click="hideNotification" class="snackbar-close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>

    <!-- Error Snackbar -->
    <div v-if="showErrorNotification" class="snackbar error-snackbar">
      <div class="snackbar-content">
        <span class="snackbar-icon">❌</span>
        <span class="snackbar-message">{{ errorMessage }}</span>
      </div>
      <button @click="hideNotification" class="snackbar-close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import { DashboardService } from '../services/dashboard'

export default {
  name: 'CompanyLeadsView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      loading: true,
      availableLeads: [], // system unassigned leads (from getAvailableLeads)
      customLeads: [], // all custom leads for this owner
      assignedLeads: [], // Array of assigned lead IDs
      assignedLeadObjects: [], // Array of full lead objects for assigned leads
      processingLeads: [],
      // Create modal
      showCreateModal: false,
      creating: false,
      createForm: {
        name: '',
        field_type: 'text',
        field_options: ['', '']
      },
      // Delete modal
      showDeleteModal: false,
      deleting: false,
      deleteTarget: null,
      // Notifications
      showSuccessNotification: false,
      showErrorNotification: false,
      successMessage: '',
      errorMessage: '',
      notificationTimeout: null
    }
  },
  computed: {
    allAvailableLeads () {
      // Merge system unassigned leads + all custom leads
      const customNotSystem = this.customLeads.filter(
        cl => !this.availableLeads.some(al => al.id === cl.id)
      )
      return [...this.availableLeads, ...customNotSystem]
    },
    unassignedLeads () {
      return this.allAvailableLeads.filter(lead => !this.assignedLeads.includes(lead.id))
    }
  },
  async mounted () {
    await this.loadData()
  },
  methods: {
    async loadData () {
      try {
        this.loading = true

        const [availableResult, companyResult, customResult] = await Promise.all([
          DashboardService.getAvailableLeads(),
          DashboardService.getCompanyLeads(),
          DashboardService.getCustomLeads()
        ])

        if (availableResult.success) {
          this.availableLeads = availableResult.leads
        }

        if (customResult.success) {
          this.customLeads = customResult.leads
        }

        if (companyResult.success) {
          // Mark custom lead objects that come from company leads
          const assigned = companyResult.companyLeads.map(lead => {
            const custom = this.customLeads.find(cl => cl.id === lead.id)
            return custom ? { ...lead, is_custom: true, field_type: custom.field_type } : lead
          })
          this.assignedLeadObjects = assigned
          this.assignedLeads = assigned.map(cl => cl.id)
        }
      } catch (error) {
        console.error('Error loading lead data:', error)
        this.displayErrorNotification('Failed to load lead data')
      } finally {
        this.loading = false
      }
    },

    async assignLead (leadId) {
      if (this.processingLeads.includes(leadId)) return
      try {
        this.processingLeads.push(leadId)
        const result = await DashboardService.assignLeadToCompany(leadId)
        if (result.success) {
          this.assignedLeads.push(leadId)
          const leadObj = this.allAvailableLeads.find(l => l.id === leadId)
          if (leadObj) this.assignedLeadObjects.push(leadObj)
          this.displaySuccessNotification(`"${this.getLeadNameById(leadId)}" added to requirements`)
        } else {
          this.displayErrorNotification(result.message || 'Failed to add lead requirement')
        }
      } catch (error) {
        this.displayErrorNotification('An error occurred while adding the lead requirement')
      } finally {
        this.processingLeads = this.processingLeads.filter(id => id !== leadId)
      }
    },

    async removeLead (leadId) {
      if (this.processingLeads.includes(leadId)) return
      try {
        this.processingLeads.push(leadId)
        const result = await DashboardService.removeLeadFromCompany(leadId)
        if (result.success) {
          this.assignedLeads = this.assignedLeads.filter(id => id !== leadId)
          this.assignedLeadObjects = this.assignedLeadObjects.filter(lead => lead.id !== leadId)
          this.displaySuccessNotification(`"${this.getLeadNameById(leadId)}" removed from requirements`)
        } else {
          this.displayErrorNotification(result.message || 'Failed to remove lead requirement')
        }
      } catch (error) {
        this.displayErrorNotification('An error occurred while removing the lead requirement')
      } finally {
        this.processingLeads = this.processingLeads.filter(id => id !== leadId)
      }
    },

    // ---- Create Custom Field ----
    openCreateModal () {
      this.createForm = { name: '', field_type: 'text', field_options: ['', ''] }
      this.showCreateModal = true
    },

    closeCreateModal () {
      if (this.creating) return
      this.showCreateModal = false
    },

    addOption () {
      this.createForm.field_options.push('')
    },

    removeOption (idx) {
      this.createForm.field_options.splice(idx, 1)
    },

    async submitCreateForm () {
      const name = this.createForm.name.trim()
      if (!name) return
      if (this.createForm.field_type === 'select') {
        const opts = this.createForm.field_options.filter(o => o.trim() !== '')
        if (opts.length < 1) {
          this.displayErrorNotification('Please add at least one option for the dropdown field')
          return
        }
      }
      try {
        this.creating = true
        const payload = {
          name: name,
          field_type: this.createForm.field_type,
          field_options: this.createForm.field_type === 'select'
            ? this.createForm.field_options.filter(o => o.trim() !== '')
            : []
        }
        const result = await DashboardService.createCustomLead(payload)
        if (result.success && result.lead) {
          this.customLeads.push(result.lead)
          this.showCreateModal = false
          this.displaySuccessNotification(`Custom field "${name}" created`)
        } else {
          this.displayErrorNotification(result.message || 'Failed to create custom field')
        }
      } catch (error) {
        this.displayErrorNotification('An error occurred while creating the custom field')
      } finally {
        this.creating = false
      }
    },

    // ---- Delete Custom Field ----
    confirmDeleteCustomField (lead) {
      this.deleteTarget = lead
      this.showDeleteModal = true
    },

    cancelDelete () {
      if (this.deleting) return
      this.showDeleteModal = false
      this.deleteTarget = null
    },

    async executeDeleteCustomField () {
      if (!this.deleteTarget) return
      try {
        this.deleting = true
        const result = await DashboardService.deleteCustomLead(this.deleteTarget.id)
        if (result.success) {
          const deletedId = this.deleteTarget.id
          this.customLeads = this.customLeads.filter(cl => cl.id !== deletedId)
          this.assignedLeads = this.assignedLeads.filter(id => id !== deletedId)
          this.assignedLeadObjects = this.assignedLeadObjects.filter(l => l.id !== deletedId)
          this.displaySuccessNotification(`Custom field "${this.deleteTarget.name}" deleted`)
          this.showDeleteModal = false
          this.deleteTarget = null
        } else {
          this.displayErrorNotification(result.message || 'Failed to delete custom field')
        }
      } catch (error) {
        this.displayErrorNotification('An error occurred while deleting the custom field')
      } finally {
        this.deleting = false
      }
    },

    // ---- Helpers ----
    getLeadNameById (leadId) {
      let lead = this.assignedLeadObjects.find(l => l.id === leadId)
      if (!lead) lead = this.allAvailableLeads.find(l => l.id === leadId)
      return lead ? lead.name : 'Unknown'
    },

    getLeadIcon (lead) {
      if (lead.is_custom) {
        const typeIcons = {
          number: '#️⃣', select: '📝', textarea: '📄'
        }
        return typeIcons[lead.field_type] || '🔧'
      }
      const iconMap = {
        Name: '👤',
        'Email Address': '📧',
        'Phone Number': '📞',
        'Company Name': '🏢',
        'Job Title': '💼',
        Age: '🎂',
        Gender: '⚧️',
        'Location or Address': '📍',
        'Business Activity': '🏭',
        'National ID / Passport ID': '🆔'
      }
      return iconMap[lead.name] || '📋'
    },

    getLeadDescription (lead) {
      if (lead.is_custom) {
        const typeLabels = {
          text: 'Custom text field',
          number: 'Custom number field',
          select: 'Custom dropdown field',
          textarea: 'Custom long text field'
        }
        return typeLabels[lead.field_type] || 'Custom lead field'
      }
      const descriptionMap = {
        Name: 'Full name of the contact person',
        'Email Address': 'Primary email address for communication',
        'Phone Number': 'Contact phone number',
        'Company Name': 'Name of the company or organization',
        'Job Title': 'Position or role within the company',
        Age: 'Age of the contact person',
        Gender: 'Gender identification',
        'Location or Address': 'Physical address or location',
        'Business Activity': 'Type of business or industry',
        'National ID / Passport ID': 'Government identification number'
      }
      return descriptionMap[lead.name] || 'Lead information field'
    },

    displaySuccessNotification (message) {
      this.hideNotification()
      this.successMessage = message
      this.showSuccessNotification = true
      this.notificationTimeout = setTimeout(() => this.hideNotification(), 4000)
    },

    displayErrorNotification (message) {
      this.hideNotification()
      this.errorMessage = message
      this.showErrorNotification = true
      this.notificationTimeout = setTimeout(() => this.hideNotification(), 5000)
    },

    hideNotification () {
      this.showSuccessNotification = false
      this.showErrorNotification = false
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout)
        this.notificationTimeout = null
      }
    }
  },

  beforeUnmount () {
    if (this.notificationTimeout) clearTimeout(this.notificationTimeout)
  }
}
</script>

<style scoped>
.company-leads-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.leads-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 32px;
}

.header-left {}

.header-right {
  flex-shrink: 0;
  padding-top: 4px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px;
  transition: color 0.3s ease;
}

.page-icon {
  font-size: 32px;
}

.page-description {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
  transition: color 0.3s ease;
}

.btn-create-custom {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: #7c3aed;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-create-custom:hover {
  background: #6d28d9;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
}

/* Sections */
.leads-section {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px;
  transition: color 0.3s ease;
}

.section-icon {
  font-size: 22px;
  opacity: 0.8;
}

.section-description {
  color: var(--text-secondary);
  margin: 0 0 24px;
  line-height: 1.5;
  transition: color 0.3s ease;
}

/* Leads Grid Container */
.leads-grid-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.leads-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.available-section {
  border-left: 4px solid #3b82f6;
}

.required-section {
  border-left: 4px solid #10b981;
}

.count-badge {
  background: var(--text-secondary);
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 8px;
  transition: background-color 0.3s ease;
}

.available-section .count-badge { background: #3b82f6; }
.required-section .count-badge  { background: #10b981; }

.custom-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 6px;
  background: rgba(124, 58, 237, 0.12);
  color: #7c3aed;
  vertical-align: middle;
  margin-left: 6px;
}

/* Lead Cards */
.lead-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 2px solid var(--border-primary);
  border-radius: 12px;
  background: var(--bg-primary);
  transition: all 0.2s;
}

.available-card {
  border-color: var(--border-primary);
  background: var(--bg-secondary);
}

.available-card:hover {
  border-color: #3b82f6;
  background: var(--bg-tertiary);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
  transform: translateY(-2px);
}

.required-card {
  border-color: #10b981;
  background: var(--bg-secondary);
}

.required-card:hover {
  border-color: #059669;
  background: var(--bg-tertiary);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.1);
  transform: translateY(-2px);
}

.lead-card.processing {
  opacity: 0.7;
  pointer-events: none;
}

.lead-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.lead-icon {
  font-size: 22px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  flex-shrink: 0;
}

.available-card .lead-icon { background: rgba(59, 130, 246, 0.1); }
.required-card  .lead-icon { background: rgba(16, 185, 129, 0.1); }

.lead-details { flex: 1; min-width: 0; }

.lead-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 3px;
  transition: color 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lead-description {
  font-size: 12px;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.3;
  transition: color 0.3s ease;
}

/* Action Buttons */
.lead-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.btn-assign,
.btn-remove {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 7px 14px;
  border: 1px solid;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 76px;
  justify-content: center;
}

.btn-assign {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.btn-assign:hover:not(:disabled) { background: #2563eb; border-color: #2563eb; }

.btn-remove {
  background: #ef4444;
  color: white;
  border-color: #ef4444;
}

.btn-remove:hover:not(:disabled) { background: #dc2626; border-color: #dc2626; }

.btn-assign:disabled,
.btn-remove:disabled { opacity: 0.7; cursor: not-allowed; }

.btn-delete-custom {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  background: transparent;
  border: 1px solid var(--border-primary);
  border-radius: 7px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.btn-delete-custom:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
  color: #ef4444;
}

.btn-delete-custom:disabled { opacity: 0.4; cursor: not-allowed; }

.action-icon { font-size: 12px; }

/* Empty Section */
.empty-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px;
  color: var(--text-secondary);
  text-align: center;
  background: var(--bg-tertiary);
  border-radius: 8px;
  border: 2px dashed var(--border-secondary);
  transition: color 0.3s ease, background-color 0.3s ease, border-color 0.3s ease;
}

.empty-section .empty-icon { font-size: 32px; opacity: 0.5; }
.empty-section p { margin: 0; font-size: 14px; color: var(--text-secondary); }

/* Loading */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: var(--text-secondary);
  text-align: center;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-primary);
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  display: inline-block;
}

@keyframes spin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ===================== MODAL ===================== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 16px;
}

.modal-box {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.modal-box-sm { max-width: 400px; }

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--border-primary);
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  transition: all 0.2s;
}

.modal-close:hover { background: var(--bg-tertiary); color: var(--text-primary); }

.modal-body {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px 20px;
  border-top: 1px solid var(--border-primary);
}

.form-group { display: flex; flex-direction: column; gap: 6px; }

.form-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.required-star { color: #ef4444; }

.form-input,
.form-select {
  padding: 9px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.form-input:focus,
.form-select:focus { border-color: #7c3aed; }

.option-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.option-input { flex: 1; }

.btn-remove-option {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
}

.btn-remove-option:hover { background: rgba(239,68,68,0.1); border-color: #ef4444; color: #ef4444; }

.btn-add-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  background: var(--bg-tertiary);
  border: 1px dashed var(--border-primary);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 2px;
}

.btn-add-option:hover { border-color: #7c3aed; color: #7c3aed; }

.btn-cancel {
  padding: 9px 18px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel:hover:not(:disabled) { background: var(--bg-tertiary); }
.btn-cancel:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-confirm-create {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  background: #7c3aed;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-confirm-create:hover:not(:disabled) { background: #6d28d9; }
.btn-confirm-create:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-confirm-delete {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-confirm-delete:hover:not(:disabled) { background: #dc2626; }
.btn-confirm-delete:disabled { opacity: 0.6; cursor: not-allowed; }

.delete-warning {
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0;
}

.delete-warning strong { color: var(--text-primary); }

/* Snackbar */
.snackbar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  z-index: 3000;
  min-width: 300px;
  max-width: 500px;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateX(-50%) translateY(20px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
}

.success-snackbar {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.error-snackbar {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.snackbar-content { display: flex; align-items: center; gap: 10px; flex: 1; }
.snackbar-icon { font-size: 18px; flex-shrink: 0; }
.snackbar-message { font-size: 14px; font-weight: 500; line-height: 1.4; }

.snackbar-close {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  opacity: 0.8;
}

.snackbar-close:hover { opacity: 1; background: rgba(255,255,255,0.1); }

/* Mobile */
@media (max-width: 768px) {
  .main-content { margin-left: 0; padding-top: 64px; }
  .page-content { padding: 16px; }
  .page-header { flex-direction: column; align-items: stretch; }
  .page-title { font-size: 24px; }
  .leads-grid-container { grid-template-columns: 1fr; gap: 20px; }

  .available-card,
  .required-card { flex-wrap: wrap; gap: 12px; }

  .lead-actions { width: 100%; }
  .btn-assign, .btn-remove { flex: 1; }

  .snackbar {
    left: 16px; right: 16px;
    transform: none;
    min-width: auto; max-width: none;
  }

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
}
</style>
